import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NewConversationButton from './NewConversationButton';
// MINSKY-16657: history list/get/delete now goes through the agentic ai-assistant service
// (MINSKY-16656's `GET /conversations` / `DELETE /conversations/:id`) instead of the legacy
// `chatApi.js`, matching `AIAssistantChatPanel.jsx`'s cutover to `useAgenticRunController`. The
// response shape (`{id, title, updatedAt, metadata}[]`) is unchanged (see
// `agenticRunApi.js`/`conversation-history.ts` header comments), so nothing else in this
// component needed to change.
import {deleteAgenticConversation, listAgenticConversations} from '../services/agenticRun/agenticRunApi';
import {conversationsForChart} from '../utils/chartConversationHistory';
// MINSKY-16473 ticket 13: the agentic client now shares dash-api's own base URL convention
// instead of the retired standalone-service `AI_ASSISTANT_SERVICE_URL`.
import {AI_ASSISTANT_CHAT_API_BASE} from '../services/api/chatApi';
import {formatTimestamp} from '../../../utils/formatTimestamp';

/**
 * Slide-in panel listing past AI conversations.
 *
 * Props:
 *   currentThreadId  — the active conversation id (highlighted in list)
 *   apiBaseUrl       — chat backend base URL
 *   onSelect(id)     — called when user clicks a past thread
 *   onNewThread()    — called when user clicks "New conversation"
 *   onClose()        — called when user clicks the close/back button
 *   onDeleted(id)    — called after a thread is successfully deleted
 */
const ThreadHistory = ({
    currentThreadId,
    apiBaseUrl,
    onSelect,
    onNewThread,
    onClose,
    onDeleted,
    chartId = null,
    historyScope = 'all',
    onHistoryScopeChange,
}) => {
    const [threads, setThreads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmId, setConfirmId] = useState(null);

    const baseUrl = apiBaseUrl || AI_ASSISTANT_CHAT_API_BASE;
    const visibleThreads = conversationsForChart(threads, chartId, chartId ? historyScope : 'all');

    const refresh = useCallback(() => {
        setLoading(true);
        setError(null);
        listAgenticConversations(baseUrl)
            .then((data) => setThreads(Array.isArray(data) ? data : []))
            .catch((e) => setError(e.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }, [baseUrl]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleDeleteClick = useCallback((e, id) => {
        e.stopPropagation();
        setConfirmId(id);
    }, []);

    const handleConfirmDelete = useCallback(async (e, id) => {
        e.stopPropagation();
        setConfirmId(null);
        setDeletingId(id);
        try {
            await deleteAgenticConversation(id, baseUrl);
            setThreads((prev) => prev.filter((t) => t.id !== id));
            if (typeof onDeleted === 'function') {
                onDeleted(id);
            }
        } catch (err) {
            setError(err.message || 'Failed to delete');
        } finally {
            setDeletingId(null);
        }
    }, [baseUrl, onDeleted]);

    const handleCancelDelete = useCallback((e) => {
        e.stopPropagation();
        setConfirmId(null);
    }, []);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            bgcolor: 'background.paper',
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                flexShrink: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <Tooltip title="Back to chat" placement="right" enterDelay={600} enterNextDelay={600}>
                    <IconButton size="small" onClick={onClose}>
                        <ChevronLeftIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
                <Typography
                    variant="body2"
                    noWrap
                    sx={{flex: 1, mx: 0.5}}
                >
                    Conversation history
                </Typography>
                <NewConversationButton onClick={onNewThread}/>
            </Box>
            {chartId ? (
                <Box sx={{px: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider'}}>
                    <ButtonGroup
                        size="small"
                        fullWidth
                        aria-label="Conversation history scope"
                        sx={{'& .MuiButtonGroup-grouped': {flex: 1}}}
                    >
                        <Button
                            variant={historyScope === 'chart' ? 'contained' : 'outlined'}
                            onClick={() => onHistoryScopeChange?.('chart')}
                            aria-pressed={historyScope === 'chart'}
                        >
                            This Chart
                        </Button>
                        <Button
                            variant={historyScope === 'all' ? 'contained' : 'outlined'}
                            onClick={() => onHistoryScopeChange?.('all')}
                            aria-pressed={historyScope === 'all'}
                        >
                            All
                        </Button>
                    </ButtonGroup>
                </Box>
            ) : null}

            {/* List */}
            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
                {loading && (
                    <Box sx={{display: 'flex', justifyContent: 'center', pt: 2}}>
                        <CircularProgress size={18}/>
                    </Box>
                )}
                {!loading && error && (
                    <Typography variant="caption" color="error" sx={{px: 1.5, py: 1, display: 'block'}}>
                        {error}
                    </Typography>
                )}
                {!loading && !error && visibleThreads.length === 0 && (
                    <Typography variant="caption" color="text.secondary"
                                sx={{px: 1.5, py: 2, display: 'block', textAlign: 'center'}}>
                        No past conversations yet.
                    </Typography>
                )}
                {!loading && visibleThreads.length > 0 && (
                    <List dense disablePadding>
                        {visibleThreads.map((t, idx) => {
                            const isActive = t.id === currentThreadId;
                            const isDeleting = deletingId === t.id;
                            const isConfirming = confirmId === t.id;
                            const docTitle = t.metadata?.documentTitle || t.metadata?.reportId || null;
                            return (
                                <React.Fragment key={t.id}>
                                    {idx > 0 && <Divider component="li"/>}
                                    <ListItemButton
                                        selected={isActive}
                                        onClick={() => !isConfirming && onSelect(t.id)}
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            '&:hover .thread-delete-btn': {opacity: 1},
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography noWrap variant="body2"
                                                            sx={{fontSize: 12, fontWeight: isActive ? 600 : 400}}>
                                                    {t.title || 'Untitled'}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography noWrap variant="caption"
                                                            sx={{fontSize: 10, color: 'text.disabled'}}>
                                                    {docTitle ? `${docTitle} · ` : ''}{formatTimestamp(t.updatedAt, '')}
                                                </Typography>
                                            }
                                        />

                                        {/* Delete action area */}
                                        {isDeleting ? (
                                            <CircularProgress size={14} sx={{ml: 0.5, flexShrink: 0}}/>
                                        ) : isConfirming ? (
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, ml: 0.5}}>
                                                <Tooltip title="Confirm delete" placement="top" enterDelay={600} enterNextDelay={600}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleConfirmDelete(e, t.id)}
                                                        sx={{
                                                            p: 0.25,
                                                            color: 'error.main',
                                                            '&:hover': {bgcolor: 'error.light', color: 'error.contrastText'},
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon sx={{fontSize: 14}}/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Cancel" placement="top" enterDelay={600} enterNextDelay={600}>
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        onClick={handleCancelDelete}
                                                        sx={{
                                                            fontSize: 10,
                                                            color: 'text.secondary',
                                                            cursor: 'pointer',
                                                            px: 0.5,
                                                            '&:hover': {color: 'text.primary'},
                                                        }}
                                                    >
                                                        cancel
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        ) : (
                                            <Tooltip title="Delete thread" placement="top" enterDelay={600} enterNextDelay={600}>
                                                <IconButton
                                                    className="thread-delete-btn"
                                                    size="small"
                                                    onClick={(e) => handleDeleteClick(e, t.id)}
                                                    sx={{
                                                        p: 0.25,
                                                        ml: 0.5,
                                                        flexShrink: 0,
                                                        opacity: 0,
                                                        transition: 'opacity 0.15s',
                                                        color: 'text.disabled',
                                                        '&:hover': {color: 'error.main'},
                                                    }}
                                                >
                                                    <DeleteOutlineIcon sx={{fontSize: 14}}/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </ListItemButton>
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </Box>
        </Box>
    );
};

export default ThreadHistory;
